import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { ApiService } from '@shared/services/api.service'
import { FuelRecord } from '@app/modules/client/fuel/fuel-records/fuel-record.model'
import { toFormData } from '@core/helpers/to-form-data'
import { Fuel, FuelStation, FuelType, Vehicle } from '@shared/models/vehicle.model'
import { UploadedFile } from '@shared/models/uploaded-file'
import { FuelSummary } from '@app/modules/client/fuel/fuel-summary.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { FilterValues } from '@shared/models/filter-values.model'
import { PagesData } from '@app/shared/models/pages-data.model'

export interface FuelImport {
  data: {
    id: number
    refueled: number
    refueledFuelType: FuelType
    transactionDate: string
    vehicle: Vehicle | []
    comments: {
      error?: string[]
      warning?: string[]
    }
  }[]
  file: UploadedFile
}

export interface CheckDisplayFieldsResponse {
  data: {
    isDisplayAdditionalFields: boolean
  }
}

@Injectable({ providedIn: 'root' })
export class FuelService {
  constructor(private apiService: ApiService) {}

  getFuelRecords(pageIndex = 0, pageSize = 10, sort?: string, filterValues?: FilterValues, fields?: (keyof FuelRecord)[]) {
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<FuelRecord>('/fuel-cards/json', pageIndex, pageSize, sort, filterValues, fields)
  }

  getSummary(
    pageIndex: number,
    pageSize: number,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof FuelSummary)[],
  ): Observable<FuelSummary[]> {
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable<FuelSummary>('/fuel-summary-report', pageIndex, pageSize, sort, filterValues, fields) as Observable<
      FuelSummary[]
    >
  }

  importFile(file: FileList): Observable<FuelImport> {
    const formData = toFormData({ files: [file] })
    return this.apiService.post<FuelImport>('/fuel-cards/upload', formData)
  }

  saveFile(fileId: string | number): Observable<FuelImport> {
    return this.apiService.post<FuelImport>(`/fuel-cards/${fileId}`, null)
  }

  exportFileRecords(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/fuel-cards/${type}`, columns, sort, filterValues)
  }

  exportFileSummary(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/fuel-summary-report/${type}`, columns, sort, filterValues)
  }

  checkDisplayFields(): Observable<boolean> {
    return this.apiService
      .get<CheckDisplayFieldsResponse>('/fuel-cards/check-display-fields')
      .pipe(map((response) => response['data'].isDisplayAdditionalFields))
  }

  getFuel(): Observable<Fuel[]> {
    return this.apiService.get(`/fuel-types`).pipe(map((response) => <Fuel[]>response['data']))
  }

  getFuelStations(pageIndex = 0, pageSize = 10, sort?: string, filterValues?: FilterValues): Observable<PagesData<FuelStation[]>> {
    // TODO: switch when Go supports paginated list
    return this.apiService.getPageable('/fuel/station', pageIndex, pageSize, sort, filterValues) as Observable<PagesData<FuelStation[]>>
  }

  assignTransaction(recordId: number, vehicleId: number): Observable<void> {
    return this.apiService.post(`/fuel-cards/record/${recordId}`, { vehicleId })
  }
}
